import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Message, Recipient } from '@microsoft/microsoft-graph-types';
import { MailContext } from './Context';
import { useAxios } from '../../Helpers/AxiosHelper';
import { Button, Card, DDReservations, DDStatus, Editor, ProgressBar, SCButton, SCIcon, Toast } from '../../Common/Imports';
import { ApiResponse, ReservationCommunications_EXT, Reservations_EXT } from '../../Models/Classes';
import ReservationDetail from '../Reservations/Reservation_Detail';
import { ComunicationStatus } from '../../Models/Enums';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../Helpers/ToastHelper';
import { PermissionHelper, usePermissions } from '../../Helpers/Permissions/PermissionContext';
import DOMPurify from 'dompurify';

interface AppMailDetailProps {
    Mail?: Message | undefined;
    IDOutlook?: string;
    ShowReservationInfos?: boolean;
    ShowSend?: boolean;
    onChangeInternalReservationStatus?: () => void;
    onChangeComunicationStatus?: () => void;
}

function AppMailDetail(props: AppMailDetailProps) {
    const P_EditMails = { idAspNetRole: usePermissions().userRole, idItem: "0", idPermission: 12 };
    const params = useParams<{ id: string }>();
    const AxiosHelper = useAxios();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const toast = useRef<Toast | null>(null);
    const [mail, setMail] = useState<Message | undefined>(props.Mail);
    const [reservation, setReservation] = useState<Reservations_EXT | undefined>();
    const [comunication, setComunication] = useState<ReservationCommunications_EXT | undefined>();
    const [selectedReservation, setSelectedReservation] = useState<number | undefined>();
    const [loading, setLoading] = useState<boolean>(false);
    const [ShowReservationPopup, setShowReservationPopup] = useState<boolean>(false);
    const [newMail, setNewMail] = useState<Message>({
        id: '',
        subject: '',
        body: {
            contentType: 'html',
            content: ''
        },
        from: {
            emailAddress: {
                address: '',
                name: ''
            }
        },
        toRecipients: [],
        hasAttachments: false,
        importance: 'normal',
        isRead: false,
    });

    const navigate = useNavigate();
    const { onSend } = useContext(MailContext);
    const id = props.IDOutlook || params.id;

    const sendMail = async () => {
        if (newMail.body?.content) {
            const _From = mail?.from ?? { emailAddress: { address: "reservationcheckhv@stayconfirmed.com" } };
            const updatedMail = {
                ...newMail,
                from: _From,
                toRecipients: mail!.from ? [mail!.from] : mail!.toRecipients,
                subject: mail!.subject,
            };

            const sent = await onSend(updatedMail);
            if (sent) {
                navigate('/apps/mail/inbox');
            }
        }
    };

    const GetData = useCallback(async () => {
        if (!id) return; // Avoid fetching if IDOutlook is not available
        setLoading(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Message>>(`Email/GetMessage/reservationcheckhv@stayconfirmed.com/${id}`);
            if (resp.data.code === 0) {
                setMail(resp.data.data);
            } else {
                setMail(undefined);
            }
        } catch (error) {
            setMail(undefined);
            console.error('Error fetching mail data:', error);
        } finally {
            setLoading(false);
        }
    }, [AxiosHelper, id]);

    const GetReservation = useCallback(async () => {
        if (!mail?.id) return; // Avoid fetching if mail is not available
        setLoading(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<Reservations_EXT | undefined>>(`Reservation/GetReservationByMailId/${mail.id}`);
            if (resp.data.code === 0) {
                setReservation(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching reservation data:', error);
        } finally {
            setLoading(false);
        }
    }, [AxiosHelper, mail?.id]);

    useEffect(() => {
        GetData();
    }, [GetData, props.IDOutlook, params.id]); // Add dependencies to refetch when IDOutlook changes

    useEffect(() => {
        if (mail) {
            GetReservation();
        }
    }, [GetReservation, mail]);

    const GetComunication = useCallback(async () => {
        const _ID = mail?.id ? mail.id : props.IDOutlook;
        setLoading(true);
        try {
            const resp = await AxiosHelper.get<ApiResponse<ReservationCommunications_EXT | undefined>>(`Operative/GetComunication/${_ID}`);
            if (resp.data.code === 0) {
                setComunication(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching reservation data:', error);
        } finally {
            setLoading(false);
        }
    }, [AxiosHelper, mail, props.IDOutlook]);

    useEffect(() => {
        GetComunication();
    }, [GetComunication, props.IDOutlook, params.id]);

    const createReservationComunication = async () => {
        if (selectedReservation) {
            const _T: ReservationCommunications_EXT = {
                idReservation: selectedReservation,
                idOutlook: mail?.id,
                idOutlookConversation: mail?.conversationId ?? undefined,
                dateReceiveEmail: mail?.receivedDateTime ? new Date(mail.receivedDateTime) : undefined,
                status: ComunicationStatus.Read,
                note: mail?.subject as string | undefined
            }
            setLoading(true);
            try {
                const resp = await AxiosHelper.post<ApiResponse<Reservations_EXT | undefined>>(`Reservation/CreateReservationComunication`, _T);
                if (resp.data.code === 0) {
                    GetReservation();
                }
            } catch (error) {
                console.error('Error creating reservation communication:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const ChangeReservationStatus = async (e: number | undefined) => {
        if (reservation && e) {
            reservation.idReservationOperationalStatus = e;
            try {
                await AxiosHelper.post(`Reservation/UpdateReservation`, reservation);
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetReservation();
            } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.Update.Error"));
            }
        }
    }
    const SetComunicationRead = async () => {
        const _ID = mail?.id ? mail.id : props.IDOutlook;
        if (comunication && reservation) {
            try {
                await AxiosHelper.get(`Operative/SetMailAsRead/reservationcheckhv@stayconfirmed.com` +
                    `/${reservation.idReservation}` +
                    `/${comunication.idReservationComunication}` +
                    `/${_ID}`);
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetComunication();
                if (props.onChangeComunicationStatus)
                    props.onChangeComunicationStatus();
            } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.Update.Error"));
            }
        }
    }
    const SetComunicationError = async () => {
        const _ID = mail?.id ? mail.id : props.IDOutlook;
        if (comunication && reservation) {
            try {
                await AxiosHelper.get(`Operative/SetMailAsError/reservationcheckhv@stayconfirmed.com` +
                    `/${reservation.idReservation}` +
                    `/${comunication.idReservationComunication}` +
                    `/${_ID}`);
                showToast('success', 'Success', t("Common.Toasts.Update.Success"));
                GetComunication();
                if (props.onChangeComunicationStatus)
                    props.onChangeComunicationStatus();
            } catch (error: unknown) {
                console.log(error);
                showToast('error', 'Error', t("Common.Toasts.Update.Error"));
            }
        }
    }

    const CardHeader = () => {
        return (
            <div className="flex flex-column md:flex-row align-items-center justify-content-between p-3">
                <div className="flex align-items-center justify-content-start">
                    <div className="flex flex-column mx-3">
                        <span className="block text-900 font-bold text-lg">FROM: {mail?.from?.emailAddress?.name}</span>
                        <span className="block text-900 font-semibold">TO: {mail?.toRecipients?.map((recipient: Recipient) => recipient.emailAddress?.address).join(', ')}</span>
                    </div>
                </div>
                <PermissionHelper requiredPermission={P_EditMails}>
                    {
                        props.ShowReservationInfos &&
                        <div className="flex align-items-center justify-content-center">
                            <div className="p-inputgroup flex-1 me-3">
                                {
                                    reservation &&
                                    <button className="p-inputgroup-addon border-end-0 bg-white" onClick={() => setShowReservationPopup(true)}>
                                        <SCIcon Name="Info" Classes="text-info"></SCIcon>
                                    </button>
                                }
                                <DDReservations Reservation={reservation} onChange={setSelectedReservation}></DDReservations>
                                <button className="p-inputgroup-addon bg-white" onClick={createReservationComunication}>
                                    <SCIcon Name="Save" Classes="text-primary"></SCIcon>
                                </button>
                            </div>{
                                reservation &&
                                <>
                                    <div className="p-inputgroup flex-1 me-3">
                                        <DDStatus IDType={2} onChange={ChangeReservationStatus} value={reservation?.idReservationOperationalStatus}></DDStatus>
                                    </div>
                                    <div className="p-inputgroup flex-1 me-3">
                                        <SCButton Name="MarkAsRead" OnClick={SetComunicationRead} Label="Set As Read" />
                                    </div>
                                    <div className="p-inputgroup flex-1 me-3">
                                        <SCButton Name="MarkAsBad" OnClick={SetComunicationError} Label="Set As Error" />
                                    </div>
                                </>
                            }
                        </div>
                    }
                </PermissionHelper>

                <div className="flex align-items-center justify-content-end column-gap-3 px-4 md:px-0">
                    <span className="text-900 font-semibold white-space-nowrap mr-auto">
                        {mail?.sentDateTime ? new Date(mail.sentDateTime).toLocaleString() : "N/A"}
                    </span>
                </div>
            </div>
        )
    }

    const CardFooter = () => {
        return (
            props.ShowSend &&
            <div className="flex justify-content-end">
                <Button type="button" icon="pi pi-send" label="Send" onClick={sendMail}></Button>
            </div>
        )
    }

    return (
        loading ?
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
            :
            mail &&
            <Card header={CardHeader} footer={CardFooter}>
                {
                    ShowReservationPopup &&
                    <ReservationDetail
                        Reservation={reservation}
                        OnHide={() => {
                            setShowReservationPopup(false);
                        }}
                    />
                }
                <Toast ref={toast}></Toast>

                <div className="surface-border border-1 border-round p-4 bg-white">
                    <div
                        className="line-height-3 mt-0 mb-3"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(mail.body?.content ?? '', {
                                USE_PROFILES: { html: true },
                                FORBID_TAGS: ['form', 'iframe', 'object', 'embed', 'svg', 'math']
                            })
                        }}
                    />
                    {
                        props.ShowSend &&
                        <Editor
                            style={{ height: '250px' }}
                            value={newMail.body?.content ?? ""}
                            onTextChange={(e) =>
                                setNewMail((prevState) => ({
                                    ...prevState,
                                    body: { ...prevState.body, content: e.htmlValue ?? '' }
                                }))
                            }>
                        </Editor>
                    }
                </div>
            </Card>
    );
}

export default AppMailDetail;
